import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      ref={ref}
      className="py-24 md:py-32 bg-[#F5F5F0]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#2D2D2D] mb-6 md:mb-8"
            style={{ fontFamily: "'DM Serif Display', serif" }}
            data-testid="about-title"
          >
            ProBwa : Votre distributeur <span className="text-[#8BC34A]">REBO Bamboo</span> à La Réunion
          </h2>
          <p className="text-base md:text-xl text-[#3A3A3A] max-w-4xl mx-auto leading-relaxed">
            ProBwa est le distributeur des produits REBO Bamboo à La Réunion. 
            Nous vous proposons une gamme complète de bambou tressé carbonisé 
            certifié pour vos terrasses, bardages et aménagements intérieurs. 
            Conseil, expertise et accompagnement de votre projet.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/w0bqjqh2_Gemini_Generated_Image_jfh4fsjfh4fsjfh4.png"
                alt="Terrasse ProBwa avec vue sur mer"
                className="w-full h-auto object-contain"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full flex flex-col items-center justify-center text-center border-2 border-[#c0c0c0] shadow-lg"
              style={{
                background: 'linear-gradient(145deg, #d4d4d4, #a8a8a8)',
                boxShadow: 'inset 0 1px 3px rgba(255,255,255,0.5), inset 0 -1px 3px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.3)',
              }}
            >
              <div className="text-[11px] font-black text-[#2D2D2D] leading-none tracking-tight">ECO</div>
              <div className="text-[7px] font-bold text-[#3A3A3A] leading-none mt-0.5 tracking-wide">BOIS</div>
              <div className="text-[7px] font-bold text-[#3A3A3A] leading-none tracking-wide">CONCEPT</div>
              <div className="w-8 h-px bg-[#8BC34A] mt-0.5 mb-0.5" />
              <div className="text-[5px] font-semibold text-[#555] tracking-widest">QUALITÉ</div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3
              className="text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-6"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              Choisir ProBwa, c'est choisir l'<span className="text-[#8BC34A]">écologie</span>
            </h3>
            <p className="text-lg text-[#3A3A3A] mb-8 leading-relaxed">
              Le bambou tressé est une alternative responsable aux bois tropicaux. 
              Ressource renouvelable par excellence, il pousse sans pesticides 
              et se régénère naturellement après chaque coupe. 
              Comme tout bois noble, un entretien régulier préserve 
              sa beauté au fil des saisons.
            </p>
            
            <Link
              to="/le-bambou"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold tracking-wide text-[#2D2D2D] hover:scale-105 transition-all duration-300"
              style={{
                background: 'linear-gradient(145deg, #e0e0e0, #a8a8a8)',
                boxShadow: 'inset 0 0 0 2px #8BC34A, inset 0 1px 3px rgba(255,255,255,0.6), inset 0 -1px 3px rgba(0,0,0,0.15), 0 4px 15px rgba(0,0,0,0.3)',
                border: '3px solid #b0b0b0',
              }}
              data-testid="about-cta"
            >
              En savoir plus sur le bambou
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
